import React, { useState } from 'react';
import {
  AlertCircle, ChevronDown, ChevronUp, Send, Upload, Link, User, Mail, Phone,
  CheckCircle, X, FileText, Image, Loader2
} from 'lucide-react';
import { dataCorrections, storage } from '../lib/supabase';

// ==================== CATEGORIES & SECTIONS ====================
const CATEGORIES = [
  {
    id: 'school_info',
    label: 'School Information',
    sections: ['School Details', 'Contact Information', 'Facilities', 'Programs Offered', 'Staff Information', 'Other']
  },
  {
    id: 'exam_results',
    label: 'Exam Results & Rankings',
    sections: ['CSEC Results', 'CAPE Results', 'PEP Results', 'School Rankings', 'Pass Rates', 'Other']
  },
  {
    id: 'enrollment',
    label: 'Enrollment & Admissions',
    sections: ['Requirements', 'Application Process', 'Deadlines', 'Fees', 'Other']
  },
  {
    id: 'exams',
    label: 'Exam Information',
    sections: ['PEP Exam', 'CSEC Exam', 'CAPE Exam', 'NVQ-J', 'Registration', 'Other']
  },
  {
    id: 'scholarships',
    label: 'Scholarships & Funding',
    sections: ['Government Scholarships', 'Private Scholarships', 'Bursaries', 'Application Process', 'Other']
  },
  {
    id: 'education_system',
    label: 'Education System',
    sections: ['School Levels', 'Pathways', 'Requirements', 'Regulations', 'Other']
  },
  {
    id: 'service_providers',
    label: 'Education Services Directory',
    sections: ['Tutors', 'Exam Prep Centres', 'School Supplies', 'Uniforms', 'Other']
  },
  {
    id: 'other',
    label: 'Other',
    sections: ['General Information', 'Website Functionality', 'Other']
  }
];

// ==================== DATA CORRECTION REPORT COMPONENT ====================
export const DataCorrectionReport = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    category: '',
    section: '',
    current_content: '',
    correct_content: '',
    proof_url: '',
    proof_image: null,
    reporter_name: '',
    reporter_email: '',
    reporter_phone: '',
    notify_when_fixed: true
  });

  const [proofType, setProofType] = useState('none'); // 'none', 'url', 'image'
  const [imagePreview, setImagePreview] = useState(null);

  const selectedCategory = CATEGORIES.find(c => c.id === formData.category);

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Image must be less than 5MB');
        return;
      }
      setFormData({ ...formData, proof_image: file });
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!formData.category) {
      setError('Please select a category');
      return;
    }
    if (!formData.current_content.trim()) {
      setError('Please describe what is currently shown');
      return;
    }
    if (!formData.correct_content.trim()) {
      setError('Please describe what it should be');
      return;
    }
    if (!formData.reporter_email.trim()) {
      setError('Please provide your email address');
      return;
    }

    setSubmitting(true);

    try {
      let proof_image_url = null;

      // Upload image if provided
      if (formData.proof_image) {
        const fileExt = formData.proof_image.name.split('.').pop();
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
        const { data: uploadData, error: uploadError } = await storage.uploadImage(
          'correction-proofs',
          fileName,
          formData.proof_image
        );

        if (uploadError) {
          console.error('Image upload error:', uploadError);
          // Continue without image if upload fails
        } else {
          proof_image_url = storage.getPublicUrl('correction-proofs', fileName);
        }
      }

      // Submit correction
      const { error: submitError } = await dataCorrections.submit({
        category: formData.category,
        section: formData.section || null,
        current_content: formData.current_content,
        correct_content: formData.correct_content,
        proof_url: proofType === 'url' ? formData.proof_url : null,
        proof_image_url: proof_image_url,
        reporter_name: formData.reporter_name || null,
        reporter_email: formData.reporter_email,
        reporter_phone: formData.reporter_phone || null,
        notify_when_fixed: formData.notify_when_fixed
      });

      if (submitError) throw submitError;

      setSubmitted(true);
    } catch (err) {
      console.error('Submission error:', err);
      const errMsg = err?.message || '';
      if (errMsg.includes('404') || errMsg.includes('not found') || errMsg.includes('relation')) {
        setError('The submission system is currently being set up. Please try again later or email your correction directly.');
      } else {
        setError('Failed to submit report. Please try again later.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      category: '',
      section: '',
      current_content: '',
      correct_content: '',
      proof_url: '',
      proof_image: null,
      reporter_name: '',
      reporter_email: '',
      reporter_phone: '',
      notify_when_fixed: true
    });
    setProofType('none');
    setImagePreview(null);
    setSubmitted(false);
    setError(null);
  };

  // Success State
  if (submitted) {
    return (
      <section className="bg-gradient-to-br from-brand-50 to-brand-50 border-2 border-brand-200 rounded-2xl p-6 mt-4">
        <div className="text-center py-4">
          <div className="w-16 h-16 bg-brand-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-brand-600" />
          </div>
          <h4 className="font-bold text-brand-900 text-xl mb-2">Thank You!</h4>
          <p className="text-brand-700 mb-4">
            Your correction report has been submitted successfully. Our team will review it shortly.
          </p>
          {formData.notify_when_fixed && (
            <p className="text-sm text-brand-600 bg-brand-100 rounded-lg px-4 py-2 inline-block">
              We'll notify you at {formData.reporter_email} when this is fixed.
            </p>
          )}
          <button
            onClick={resetForm}
            className="mt-6 px-6 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
          >
            Submit Another Report
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-2xl p-6 mt-4">
      {/* Header - Always visible */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
            <AlertCircle className="w-6 h-6 text-blue-600" />
          </div>
          <div className="text-left">
            {/* whitespace-nowrap here made this heading 279px wide, pushing the
                    document to 381px and scrolling the whole page sideways on
                    320-360px phones. It can wrap; nothing depends on one line. */}
            <h4 className="font-bold text-blue-900 text-base sm:text-lg">Inaccurate data on our website?</h4>
            <p className="text-sm text-blue-700">Help us improve our data accuracy</p>
          </div>
        </div>
        <div className="flex items-center justify-center sm:justify-end gap-2">
          <span className="text-base font-semibold text-blue-600 bg-blue-100 px-5 py-2 rounded-full whitespace-nowrap">
            Report It
          </span>
          {isOpen ? (
            <ChevronUp className="w-5 h-5 text-blue-600" />
          ) : (
            <ChevronDown className="w-5 h-5 text-blue-600" />
          )}
        </div>
      </button>

      {/* Form - Collapsible */}
      {isOpen && (
        <form onSubmit={handleSubmit} className="mt-6 space-y-5">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
              <X className="w-5 h-5" />
              {error}
            </div>
          )}

          {/* Category Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Which section has the error? *
            </label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value, section: '' })}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              required
            >
              <option value="">Select a category...</option>
              {CATEGORIES.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.label}</option>
              ))}
            </select>
          </div>

          {/* Section Selection (Dynamic) */}
          {selectedCategory && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Specific section (optional)
              </label>
              <select
                value={formData.section}
                onChange={(e) => setFormData({ ...formData, section: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                <option value="">Select a section...</option>
                {selectedCategory.sections.map(section => (
                  <option key={section} value={section}>{section}</option>
                ))}
              </select>
            </div>
          )}

          {/* What it currently says */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <FileText className="w-4 h-4 inline mr-1" />
              What does it currently say? *
            </label>
            <textarea
              value={formData.current_content}
              onChange={(e) => setFormData({ ...formData, current_content: e.target.value })}
              placeholder="Quote or describe the incorrect information..."
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[100px]"
              required
            />
          </div>

          {/* What it should be */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <CheckCircle className="w-4 h-4 inline mr-1" />
              What should it say? *
            </label>
            <textarea
              value={formData.correct_content}
              onChange={(e) => setFormData({ ...formData, correct_content: e.target.value })}
              placeholder="Provide the correct information..."
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[100px]"
              required
            />
          </div>

          {/* Proof (Optional) */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Do you have proof? (optional)
            </label>
            <div className="flex gap-2 mb-3">
              <button
                type="button"
                onClick={() => setProofType('none')}
                className={`px-4 py-2 rounded-lg border-2 transition-colors ${
                  proofType === 'none'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                No proof
              </button>
              <button
                type="button"
                onClick={() => setProofType('url')}
                className={`px-4 py-2 rounded-lg border-2 transition-colors flex items-center gap-2 ${
                  proofType === 'url'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Link className="w-4 h-4" /> URL
              </button>
              <button
                type="button"
                onClick={() => setProofType('image')}
                className={`px-4 py-2 rounded-lg border-2 transition-colors flex items-center gap-2 ${
                  proofType === 'image'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Image className="w-4 h-4" /> Image
              </button>
            </div>

            {proofType === 'url' && (
              <input
                type="url"
                value={formData.proof_url}
                onChange={(e) => setFormData({ ...formData, proof_url: e.target.value })}
                placeholder="https://example.com/source"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            )}

            {proofType === 'image' && (
              <div className="space-y-3">
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors">
                  <Upload className="w-8 h-8 text-gray-400" />
                  <span className="mt-2 text-sm text-gray-500">Click to upload image</span>
                  <span className="text-xs text-gray-400">Max 5MB</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                  />
                </label>
                {imagePreview && (
                  <div className="relative">
                    <img
                      src={imagePreview}
                      alt="Proof preview"
                      className="w-full max-h-48 object-contain rounded-lg border border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setFormData({ ...formData, proof_image: null });
                        setImagePreview(null);
                      }}
                      className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Contact Information */}
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <h5 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <User className="w-4 h-4" /> Your Contact Information
            </h5>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Name (optional)</label>
                <input
                  type="text"
                  value={formData.reporter_name}
                  onChange={(e) => setFormData({ ...formData, reporter_name: e.target.value })}
                  placeholder="Your name"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  <Mail className="w-3 h-3 inline mr-1" />
                  Email *
                </label>
                <input
                  type="email"
                  value={formData.reporter_email}
                  onChange={(e) => setFormData({ ...formData, reporter_email: e.target.value })}
                  placeholder="your@email.com"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm text-gray-600 mb-1">
                  <Phone className="w-3 h-3 inline mr-1" />
                  Phone (optional)
                </label>
                <input
                  type="tel"
                  value={formData.reporter_phone}
                  onChange={(e) => setFormData({ ...formData, reporter_phone: e.target.value })}
                  placeholder="876-XXX-XXXX"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Notify checkbox */}
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.notify_when_fixed}
              onChange={(e) => setFormData({ ...formData, notify_when_fixed: e.target.checked })}
              className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-gray-700">Notify me when this is corrected</span>
          </label>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                Submit Report
              </>
            )}
          </button>

          <p className="text-xs text-gray-500 text-center">
            We appreciate your help in keeping our information accurate. Your submission will be reviewed by our team.
          </p>
        </form>
      )}
    </section>
  );
};

export default DataCorrectionReport;
